import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { isSuperAdmin } from '../../utils/roles';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Button from '../../components/ui/Button';
import { BackupService, BackupData } from '../../services/backupService';
import {
  Download,
  Database,
  FileText,
  AlertCircle,
  CheckCircle2,
  Loader2,
  HardDrive,
  Users,
  Building2,
  DollarSign,
  CreditCard,
  Shield
} from 'lucide-react';

const DataBackup = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [backupStats, setBackupStats] = useState<{
    totalRecords: number;
    exportDate: string;
  } | null>(null);
  const [groups, setGroups] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Fetch all groups for super admin
  useEffect(() => {
    const fetchGroups = async () => {
      if (!user || !isSuperAdmin(userProfile?.role)) return;
      
      try {
        setLoadingGroups(true);
        const { data: groupsData, error: groupsError } = await supabase
          .from('groups')
          .select('id, name')
          .eq('is_active', true)
          .order('name', { ascending: true });
        
        if (groupsError) throw groupsError;
        
        const groupsList = (groupsData || []).map((g: any) => ({
          id: g.id,
          name: g.name || 'Unnamed Group'
        }));
        
        setGroups(groupsList);
        if (groupsList.length > 0 && !selectedGroupId) {
          setSelectedGroupId(groupsList[0].id);
        }
      } catch (err: any) {
        console.error('Error fetching groups:', err);
        setError(`Failed to load groups: ${err.message}`);
      } finally {
        setLoadingGroups(false);
      }
    };

    fetchGroups();
  }, [user, userProfile, selectedGroupId]);

  const handleFullBackup = async () => {
    if (!user || !isSuperAdmin(userProfile?.role)) {
      setError('Unauthorized: Only super admins can create backups');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const backupData = await BackupService.exportAllData();
      BackupService.downloadBackup(backupData);
      
      setBackupStats({
        totalRecords: backupData.metadata.totalRecords,
        exportDate: backupData.metadata.exportDate,
      });
      setSuccess(`Backup completed successfully! ${backupData.metadata.totalRecords} records exported.`);
    } catch (err: any) {
      console.error('Backup error:', err);
      setError(err.message || 'Failed to create backup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGroupBackup = async () => {
    if (!user || !isSuperAdmin(userProfile?.role)) {
      setError('Unauthorized: Only super admins can create backups');
      return;
    }

    if (!selectedGroupId) {
      setError('Please select a group first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const backupData = await BackupService.exportGroupData(selectedGroupId);
      const groupName = backupData.metadata.groupName || 'group';
      const sanitizedGroupName = groupName.replace(/[^a-z0-9]/gi, '_');
      BackupService.downloadBackup(backupData, `nasuman-group-backup-${sanitizedGroupName}-${new Date().toISOString().split('T')[0]}.json`);
      
      setBackupStats({
        totalRecords: backupData.metadata.totalRecords,
        exportDate: backupData.metadata.exportDate,
      });
      setSuccess(`Group backup completed successfully! ${backupData.metadata.totalRecords} records exported for "${groupName}".`);
    } catch (err: any) {
      console.error('Group backup error:', err);
      setError(err.message || 'Failed to create group backup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTableBackup = async (tableName: string) => {
    if (!user || !isSuperAdmin(userProfile?.role)) {
      setError('Unauthorized: Only super admins can create backups');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const backupData = await BackupService.exportAllData();
      const tableData = (backupData as any)[tableName] || [];
      
      if (tableData.length === 0) {
        setError(`No data found in ${tableName}`);
        return;
      }

      BackupService.exportTableAsCSV(tableData, tableName);
      setSuccess(`${tableName} exported successfully! ${tableData.length} records.`);
    } catch (err: any) {
      console.error('Table backup error:', err);
      setError(err.message || `Failed to export ${tableName}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  if (!user || !isSuperAdmin(userProfile?.role)) {
    return null;
  }

  const tables = [
    { name: 'user_profiles', label: 'User Profiles', icon: Users, count: 0 },
    { name: 'groups', label: 'Groups', icon: Building2, count: 0 },
    { name: 'group_members', label: 'Group Members', icon: Users, count: 0 },
    { name: 'contributions', label: 'Contributions', icon: DollarSign, count: 0 },
    { name: 'loans', label: 'Loans', icon: CreditCard, count: 0 },
    { name: 'loan_payments', label: 'Loan Payments', icon: DollarSign, count: 0 },
    { name: 'loan_requests', label: 'Loan Requests', icon: CreditCard, count: 0 },
    { name: 'subscriptions', label: 'Subscriptions', icon: Shield, count: 0 },
    { name: 'subscription_plans', label: 'Subscription Plans', icon: FileText, count: 0 },
    { name: 'payments', label: 'Payments', icon: DollarSign, count: 0 },
    { name: 'membership_fee_payments', label: 'Membership Fee Payments', icon: DollarSign, count: 0 },
  ];

  return (
    <>
      <Helmet>
        <title>Data Backup - Nasuman</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          isSidebarCollapsed={isSidebarCollapsed}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
        />

        <div className="flex">
          <Sidebar
            isMobileMenuOpen={isMobileMenuOpen}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
            isSidebarCollapsed={isSidebarCollapsed}
            setIsSidebarCollapsed={setIsSidebarCollapsed}
            currentPath="/data-backup"
            isSuperAdmin={true}
          />

          <main className={`flex-1 transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
            <div className="p-4 md:p-6 lg:p-8">
              <BreadcrumbTrail
                items={[
                  { label: 'Dashboard', path: '/super-admin-dashboard' },
                  { label: 'Data Backup', path: '/data-backup' },
                ]}
              />

              <div className="mt-6">
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
                    <HardDrive className="w-8 h-8 text-primary" />
                    Data Backup
                  </h1>
                  <p className="text-muted-foreground mt-2">
                    Export all platform data for backup purposes. Only super admins can access this feature.
                  </p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-destructive">{error}</p>
                    </div>
                  </div>
                )}

                {/* Success Message */}
                {success && (
                  <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-success">{success}</p>
                      {backupStats && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Exported {backupStats.totalRecords} records on {new Date(backupStats.exportDate).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Full Backup Section */}
                <div className="bg-card border border-border rounded-lg p-6 mb-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                        <Database className="w-5 h-5 text-primary" />
                        Full Platform Backup
                      </h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        Export all data from all tables as a single JSON file
                      </p>
                    </div>
                  </div>

                  <Button
                    onClick={handleFullBackup}
                    disabled={loading}
                    iconName="Download"
                    iconPosition="left"
                    className="w-full sm:w-auto"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Creating Backup...
                      </>
                    ) : (
                      'Download Full Backup (JSON)'
                    )}
                  </Button>
                </div>

                {/* Group-Specific Backup Section */}
                <div className="bg-card border border-border rounded-lg p-6 mb-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-primary" />
                        Group-Specific Backup
                      </h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        Export all data for a specific group including members, contributions, loans, and more
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Select Group
                      </label>
                      <select
                        value={selectedGroupId}
                        onChange={(e) => setSelectedGroupId(e.target.value)}
                        disabled={loading || loadingGroups}
                        className="w-full px-3 py-2 border border-input rounded-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingGroups ? (
                          <option>Loading groups...</option>
                        ) : groups.length === 0 ? (
                          <option>No groups available</option>
                        ) : (
                          <>
                            <option value="">-- Select a group --</option>
                            {groups.map((group) => (
                              <option key={group.id} value={group.id}>
                                {group.name}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>

                    <div className="bg-info/10 border border-info/20 rounded-lg p-4">
                      <p className="text-sm text-muted-foreground">
                        <strong className="text-foreground">Note:</strong> This backup includes:
                      </p>
                      <ul className="text-xs text-muted-foreground mt-2 list-disc list-inside space-y-1">
                        <li>Group details and settings</li>
                        <li>All members (with user profiles, roles: admin, clerk, member)</li>
                        <li>All contributions/savings</li>
                        <li>All loans and loan payments</li>
                        <li>All loan requests</li>
                        <li>Membership fee payments</li>
                        <li>Subscriptions for group members</li>
                        <li>Payment records</li>
                      </ul>
                      <p className="text-xs text-warning mt-2 font-medium">
                        ⚠️ Passwords are stored securely (hashed) and cannot be exported in plain text for security reasons.
                      </p>
                    </div>

                    <Button
                      onClick={handleGroupBackup}
                      disabled={loading || !selectedGroupId || loadingGroups}
                      iconName="Download"
                      iconPosition="left"
                      className="w-full sm:w-auto"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Creating Group Backup...
                        </>
                      ) : (
                        'Download Group Backup (JSON)'
                      )}
                    </Button>
                  </div>
                </div>

                {/* Individual Table Backups */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="mb-4">
                    <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                      <FileText className="w-5 h-5 text-primary" />
                      Export Individual Tables
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Export specific tables as CSV files
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {tables.map((table) => {
                      const IconComponent = table.icon;
                      return (
                        <div
                          key={table.name}
                          className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <IconComponent className="w-5 h-5 text-primary" />
                              <h3 className="font-medium text-foreground">{table.label}</h3>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleTableBackup(table.name)}
                            disabled={loading}
                            variant="outline"
                            size="sm"
                            iconName="Download"
                            iconPosition="left"
                            className="w-full"
                          >
                            Export CSV
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Important Notice */}
                <div className="mt-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-warning mb-1">Important Security Notice</p>
                      <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Backup files contain sensitive user data and must be stored securely</li>
                        <li>Do not share backup files with unauthorized personnel</li>
                        <li>Backup files should be encrypted when stored long-term</li>
                        <li>Regular backups are recommended for disaster recovery</li>
                        <li>Large backups may take several minutes to generate</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default DataBackup;
